import { useState } from "react";
import { Monitor, RefreshCw, Smartphone, Tablet } from "lucide-react";
import { useMatters } from "@ncp/assistant-sdk";
import { useLocale, useTranslation } from "@ncp/i18n";
import { PageHost } from "@ncp/page-host";
import { Button, InlineAlert, NativeSelect, ToggleGroup, ToggleGroupItem, cn, toast } from "@ncp/ui";
import type { PageDetail } from "../api";

type Lang = "en" | "ar" | "both";
type Width = "full" | "tablet" | "phone";

/** The version as people will see it, in English, Arabic or both side by side, in any of its slots and at three widths. */
export function PreviewTab({ detail, reloadKey }: { detail: PageDetail; reloadKey: number }) {
  const { t } = useTranslation();
  const { locale: ui } = useLocale();
  const p = detail.page;
  const [lang, setLang] = useState<Lang>(ui === "ar" ? "ar" : "en");
  const [slot, setSlot] = useState(p.slots[0] ?? "nav");
  const [space, setSpace] = useState<string | null>(p.spaces[0] ?? null);
  const [width, setWidth] = useState<Width>("full");
  const [n, setN] = useState(0);
  const needsMatter = slot === "work-tab" || slot === "workbench";
  const matters = useMatters({ workspace: space, status: "all", take: 25 }, { enabled: needsMatter && !!space });
  const [picked, setPicked] = useState<string | null>(null);
  const matterId = needsMatter ? (picked ?? matters.data?.items[0]?.id ?? null) : null;

  const frame = (locale: "en" | "ar") => (
    <div className={cn("mx-auto h-[72vh] w-full overflow-hidden rounded-xl border border-sand-200 bg-white", width === "tablet" && "max-w-[820px]", width === "phone" && "max-w-[400px]")}>
      <PageHost key={`${locale}:${slot}:${space ?? ""}:${matterId ?? ""}`} pageKey={p.key} definitionId={p.id} locale={locale} slot={slot} space={space} matterId={matterId} reloadKey={`${reloadKey}:${n}`}
        onOpen={() => toast.info(t("admin:pageStudio.preview.navigates"))} />
    </div>
  );

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <ToggleGroup type="single" size="sm" value={lang} onValueChange={(v) => v && setLang(v as Lang)} aria-label={t("admin:pageStudio.preview.language")}>
          <ToggleGroupItem value="en">English</ToggleGroupItem>
          <ToggleGroupItem value="ar">العربية</ToggleGroupItem>
          <ToggleGroupItem value="both">{t("admin:pageStudio.preview.both")}</ToggleGroupItem>
        </ToggleGroup>
        <ToggleGroup type="single" size="sm" value={width} onValueChange={(v) => v && setWidth(v as Width)} aria-label={t("admin:pageStudio.preview.width")}>
          <ToggleGroupItem value="full" aria-label={t("admin:pageStudio.preview.desktop")}><Monitor className="size-3.5" /></ToggleGroupItem>
          <ToggleGroupItem value="tablet" aria-label={t("admin:pageStudio.preview.tablet")}><Tablet className="size-3.5" /></ToggleGroupItem>
          <ToggleGroupItem value="phone" aria-label={t("admin:pageStudio.preview.phone")}><Smartphone className="size-3.5" /></ToggleGroupItem>
        </ToggleGroup>
        {p.slots.length > 1 ? (
          <NativeSelect value={slot} onChange={(e) => setSlot(e.target.value)} className="h-8 w-44" aria-label={t("admin:pageStudio.preview.slot")}>
            {p.slots.map((s) => <option key={s} value={s}>{t(`admin:pageStudio.slots.${s}`, { defaultValue: s })}</option>)}
          </NativeSelect>
        ) : null}
        {p.spaces.length > 1 ? (
          <NativeSelect value={space ?? ""} onChange={(e) => setSpace(e.target.value || null)} className="h-8 w-48" aria-label={t("admin:pageStudio.preview.space")}>
            {p.spaces.map((s) => <option key={s} value={s}>{s}</option>)}
          </NativeSelect>
        ) : null}
        {needsMatter && matters.data?.items.length ? (
          <NativeSelect value={matterId ?? ""} onChange={(e) => setPicked(e.target.value)} className="h-8 w-72" aria-label={t("admin:pageStudio.preview.matter")}>
            {matters.data.items.map((m) => <option key={m.id} value={m.id}>{m.number} · {(ui === "ar" && m.titleAr) || m.title}</option>)}
          </NativeSelect>
        ) : null}
        <Button size="sm" variant="ghost" className="ms-auto" onClick={() => setN((x) => x + 1)}><RefreshCw /> {t("admin:pageStudio.preview.reload")}</Button>
      </div>
      {p.status !== "Published" ? <InlineAlert tone="info">{t("admin:pageStudio.preview.draftNote", { version: p.version })}</InlineAlert> : null}
      {needsMatter && !matters.isLoading && !matterId ? <InlineAlert tone="warning">{t("admin:pageStudio.preview.noMatter")}</InlineAlert> : null}
      {lang === "both" ? <div className="grid gap-3 2xl:grid-cols-2">{frame("en")}{frame("ar")}</div> : frame(lang)}
    </div>
  );
}
